/* eslint-disable sort-imports */
import {useCallback, useEffect, useRef} from "react"
import Devise from "./devise.js"
import * as inflection from "inflection"
import ModelEvents from "./model-events.js"
import useQueryParams from "on-location-changed/build/use-query-params.js"
import useShape from "./use-shape.js"

/**
 * @typedef {object} useModelArgs
 * @property {(arg: object) => Function} [callback]
 * @property {(arg: object) => object} [args]
 * @property {() => number|string} [loadByQueryParam]
 * @property {any[]} [cacheArgs]
 * @property {{params: object}} [match]
 * @property {(ctx: { model: import("./base-model.js").default }) => void} [onDestroyed]
 * @property {import("./collection.js").default} [query]
 */

/**
 * @param {Function|object} modelClassArg
 * @param {object | ((args: {modelClass: typeof import("./base-model.js").default}) => useModelArgs)} [argsArg]
 */
/** useModel. */
// eslint-disable-next-line complexity
const useModel = (modelClassArg, argsArg = {}) => {
  const queryParams = useQueryParams()
  const loadModelGenerationRef = useRef(0)
  let args, modelClass

  if (typeof argsArg == "function") {
    args = argsArg({modelClass})
  } else {
    args = argsArg
  }

  const s = useShape(args)

  s.useStates({
    model: undefined,
    notFound: undefined
  })

  if ("active" in s.props && !s.props.active) {
    s.meta.active = false
  } else {
    s.meta.active = true
  }

  if (typeof modelClassArg == "object") {
    modelClass = modelClassArg.callback({queryParams})
  } else {
    modelClass = modelClassArg
  }

  const paramsVariableName = `${modelClass.modelName().paramKey()}_id`
  let modelId

  if (args.loadByQueryParam) {
    modelId = args.loadByQueryParam({queryParams})
  } else if (!args.query) {
    if (!args.match) throw new Error("Both 'loadByQueryParam' and 'match' wasn't given")

    modelId = args.match.params[paramsVariableName] || args.match.params.id
  }

  const modelVariableName = inflection.camelize(modelClass.modelClassData().name, true)
  const cacheArgs = [modelId]

  const loadExistingModel = useCallback(async (loadModelGeneration) => {
    let query

    if (s.m.modelId) {
      query = modelClass.ransack({id_eq: s.m.modelId})
    } else if (s.m.args.query) {
      query = s.m.args.query.clone()
    } else {
      throw new Error(`No model ID was given: ${s.m.modelId} by '${paramsVariableName}' in query params: ${Object.keys(s.props.match.params).join(", ")}`)
    }

    if (s.props.abilities) query.abilities(s.p.abilities)
    if (s.props.preload) query.preload(s.p.preload)
    if (s.props.select) query.select(s.p.select)

    const model = await query.first()

    if (loadModelGeneration != loadModelGenerationRef.current) return

    s.set({model, notFound: !model})
  }, [])

  const loadNewModel = useCallback(async (loadModelGeneration) => {
    const ModelClass = modelClass
    const paramKey = ModelClass.modelName().paramKey()
    const modelDataFromParams = s.m.queryParams[paramKey] || {}

    let defaults = {}

    if (s.props.newIfNoId?.defaults) {
      let defaultsResult = s.m.newIfNoIdDefaultsResult

      if (defaultsResult === null) {
        defaultsResult = s.props.newIfNoId.defaults()
      }

      s.meta.newIfNoIdDefaultsResult = null
      defaults = await defaultsResult
    }

    const modelData = Object.assign(defaults, s.props.newAttributes, modelDataFromParams)
    const model = new ModelClass({
      isNewRecord: true,
      data: {a: modelData}
    })

    if (loadModelGeneration != loadModelGenerationRef.current) return

    s.set({model})
  }, [])

  const loadModel = useCallback(async () => {
    // Only the newest model request may update state after route or auth changes.
    const loadModelGeneration = loadModelGenerationRef.current + 1

    loadModelGenerationRef.current = loadModelGeneration
    if (!s.m.active) {
      // Not active - don't do anything
    } else if (s.props.newIfNoId && !s.m.modelId) {
      return await loadNewModel(loadModelGeneration)
    } else if (!s.props.optional || s.m.modelId || s.m.args.query) {
      return await loadExistingModel(loadModelGeneration)
    }
  }, [])

  const onDestroyed = useCallback(({model}) => {
    const forwardArgs = {model}

    forwardArgs[s.m.modelVariableName] = model

    s.p.onDestroyed(forwardArgs)
  }, [])

  const onSignedIn = useCallback(() => {
    loadModel()
  }, [])

  const onSignedOut = useCallback(() => {
    loadModel()
  }, [])

  if (args.cacheArgs) {
    cacheArgs.push(...args.cacheArgs)
  }

  s.updateMeta({args, modelId, modelVariableName, queryParams})
  if (s.meta.syncNewModel == undefined) s.meta.syncNewModel = false
  if (s.meta.newIfNoIdDefaultsResult == undefined) s.meta.newIfNoIdDefaultsResult = null

  if (s.m.active && s.props.newIfNoId && !s.m.modelId && !s.s.model && !s.m.syncNewModel) {
    if (s.props.newIfNoId?.defaults && s.m.newIfNoIdDefaultsResult === null) {
      const defaultsResult = s.props.newIfNoId.defaults()

      if (defaultsResult && typeof defaultsResult.then == "function") {
        s.meta.newIfNoIdDefaultsResult = defaultsResult.catch((error) => {
          throw error
        })
      } else {
        s.meta.newIfNoIdDefaultsResult = defaultsResult
      }
    }

    if (!(s.m.newIfNoIdDefaultsResult && typeof s.m.newIfNoIdDefaultsResult.then == "function")) {
      const ModelClass = modelClass
      const paramKey = ModelClass.modelName().paramKey()
      const modelDataFromParams = s.m.queryParams[paramKey] || {}
      const modelData = Object.assign(s.m.newIfNoIdDefaultsResult || {}, s.props.newAttributes, modelDataFromParams)
      const model = new ModelClass({
        isNewRecord: true,
        data: {a: modelData}
      })

      s.meta.syncNewModel = model
      s.set({model})
    }
  }

  // Start async model loading after mount so ShapeHook state updates do not race the mount lifecycle.
  useEffect(
    () => { loadModel() },
    cacheArgs
  )

  useEffect(() => () => {
    loadModelGenerationRef.current += 1
  }, [])

  useEffect(() => {
    let reloadModelCallback

    if (args.events) {
      reloadModelCallback = args.events.addListener("reloadModel", loadModel)
    }

    return () => {
      if (reloadModelCallback) {
        args.events.removeListener("reloadModel", loadModel)
      }
    }
  }, [args.events])

  useEffect(() => {
    let connectUpdated

    if (s.s.model && args.eventUpdated) {
      connectUpdated = ModelEvents.connectUpdated(s.s.model, loadModel)
    }

    return () => {
      connectUpdated?.unsubscribe()
    }
  }, [args.eventUpdated, s.s.model?.id()])

  useEffect(() => {
    Devise.events().addListener("onDeviseSignIn", onSignedIn)
    Devise.events().addListener("onDeviseSignOut", onSignedOut)

    return () => {
      Devise.events().removeListener("onDeviseSignIn", onSignedIn)
      Devise.events().removeListener("onDeviseSignOut", onSignedOut)
    }
  })

  useEffect(() => {
    let connectDestroyed

    if (s.s.model && args.onDestroyed) {
      connectDestroyed = ModelEvents.connectDestroyed(s.s.model, onDestroyed)
    }

    return () => {
      connectDestroyed?.unsubscribe()
    }
  }, [args.onDestroyed, s.s.model?.id()])

  const result = {
    model: s.s.model,
    modelId,
    notFound: s.s.notFound
  }

  result[modelVariableName] = s.s.model
  result[`${modelVariableName}Id`] = modelId
  result[`${modelVariableName}NotFound`] = s.s.notFound

  return result
}

export default useModel
